import { EventBus } from '@/assets/js/event-bus';
import { getId } from '@/assets/js/utils';
import RoundStatus from '@/assets/js/enum/RoundStatus';
import DocumentType from '@/assets/js/enum/DocumentType';
import DocumentStatus from '@/assets/js/enum/DocumentStatus';
import PermissionLevel from '@/assets/js/enum/PermissionLevel';
import { xhr } from '@/xhr/endpoints';

//
// Helper functions
function assignAnnotationsToNodes(nodes, counterpoints) {
  if (!nodes || !counterpoints) return;

  const annotationsByNodeOrder = new Map();

  counterpoints.forEach(cpData => {
    const cpInfo = {
      _id: cpData._id,
      author: cpData.author,
      team: cpData.team,
    };

    cpData.annotations?.forEach(ann => {
      if (
        !ann.selectedText ||
        ann.selectedText.from?.nodeOrder === undefined ||
        ann.selectedText.to?.nodeOrder === undefined
      ) {
        return;
      }

      const fromOrder = ann.selectedText.from.nodeOrder;
      const toOrder = ann.selectedText.to.nodeOrder;

      for (let order = fromOrder; order <= toOrder; order++) {
        if (!annotationsByNodeOrder.has(order)) {
          annotationsByNodeOrder.set(order, []);
        }
        annotationsByNodeOrder.get(order).push({
          annotation: ann,
          counterpoint: cpInfo,
        });
      }
    });
  });

  nodes.forEach(node => {
    const nodeOrder = node?.nodeOrder;
    if (nodeOrder === undefined) {
      node.counterpointAnnotationIDs = [];
      node.counterpoints = [];
      return;
    }

    const matchingAnnotations = annotationsByNodeOrder.get(nodeOrder) || [];

    const counterpointMap = new Map();
    matchingAnnotations.forEach(({ annotation, counterpoint }) => {
      const cpId = counterpoint._id;
      if (!counterpointMap.has(cpId)) {
        counterpointMap.set(cpId, {
          _id: counterpoint._id,
          author: counterpoint.author,
          team: counterpoint.team,
          annotations: [],
        });
      }
      counterpointMap.get(cpId).annotations.push(annotation);
    });

    const nodeCounterpoints = Array.from(counterpointMap.values());

    node.counterpointAnnotationIDs = nodeCounterpoints.flatMap(cp =>
      cp.annotations.map(ann => ann._id)
    );
    node.counterpoints = nodeCounterpoints.filter(cpData => cpData.annotations.length > 0);
  });
}

function prepareCounterpointData(counterpoint) {
  const roundZero = counterpoint?.rounds?.find(round => round.round === 0);

  const activeRoundsWhole = counterpoint?.rounds
    ?.filter(round => round.round > 0)
    ?.sort((a, b) => a.round - b.round);

  const activeRounds = counterpoint?.rounds
    ?.map(round => ({
      ...round,
      annotations: round.annotations.filter(annotation => !annotation.deleted),
    }))
    ?.filter(round => round.round > 0)
    ?.sort((a, b) => a.round - b.round);

  return {
    roundZero,
    activeRoundsWhole,
    activeRounds,
  };
}

function filterAnnotationsForCurateMode(counterpoint, documentData, getters, userId) {
  if (documentData.discussionType === 'curate') {
    counterpoint?.rounds?.forEach(round => {
      round.annotations.forEach(annotation => {
        if (
          Object.keys(annotation.selectedText).length &&
          annotation?.refAnnotation?._id &&
          getId(annotation.author) !== userId
        ) {
          annotation.selectedText = {};
        }
      });
    });
  }
}

function setupTeamModeFiltering(counterpoint, state, activeRounds, latestRound) {
  const isTeamLead = state.debateStatus?.permissionLevel === PermissionLevel.TEAM_LEAD;
  const isCohort = state.debateStatus?.permissionLevel === PermissionLevel.COHORT;

  if (isCohort) {
    return { isTeamLead: false, activeTeamMembersIds: [] };
  }

  const activeTeamMembersIds = state.currentTeam?.members
    ?.filter(
      data =>
        (data.status === 'Accepted' || data.status === 'Invited') &&
        data.teamRole === PermissionLevel.COHORT
    )
    ?.map(member => member.user?._id);

  if (latestRound && isTeamLead && activeTeamMembersIds?.length >= 1) {
    counterpoint.rounds = counterpoint.rounds.map(data => {
      if (data.round === latestRound) {
        data.annotations = data.annotations?.filter(
          item =>
            activeTeamMembersIds?.includes(getId(item.author)) ||
            getId(data.author) === getId(item.author)
        );
      }
      return data;
    });
  }

  return { isTeamLead, activeTeamMembersIds };
}

async function convertInstructionsToComments(counterpoint, state, dispatch, latestRound) {
  if (
    !state.isTeamModeActive &&
    state.debateStatus?.permissionLevel === PermissionLevel.TEAM_LEAD &&
    latestRound
  ) {
    let currentRoundData = counterpoint.rounds.filter(data => data.round === latestRound)[0];

    for (let i = 0; i < currentRoundData.annotations.length; i++) {
      const element = currentRoundData.annotations[i];
      if (element.type === 'instruction') {
        let dataToUpdate = {
          comment: element.instruction,
          type: 'comment',
        };

        await dispatch('switchAnnotationType', {
          annotation: element,
          dataToUpdate,
          parentCounterpoint: currentRoundData,
        });
      }
    }
  }
}

function setupTeamReplies(state, getters) {
  if (
    !state.publicView &&
    [PermissionLevel.COHORT, PermissionLevel.TEAM_LEAD].includes(getters.permissionLevel)
  ) {
    const teamReply = {
      author: { _id: state.userData.id },
      comment: ' ',
    };

    const activeRound = getters.myLatestRoundActive ? getters.myLatestRound : null;

    if (activeRound) {
      activeRound.annotations
        .filter(
          ann =>
            (PermissionLevel.COHORT === getters.permissionLevel &&
              ['instruction', 'hostInstruction'].includes(ann.type)) ||
            (PermissionLevel.TEAM_LEAD === getters.permissionLevel &&
              'hostInstruction' === ann.type)
        )
        .forEach(ann => {
          if (!ann.teamReplies) {
            ann.teamReplies = [{ ...teamReply }];
          } else if (!ann.teamReplies.find(reply => reply.author._id === getters.userId)) {
            ann.teamReplies = [...ann.teamReplies, { ...teamReply }];
          }
        });
    }
  }
}

//
// state
const state = {
  counterpointData: null,
  counterpointWholeData: null,
  selectedCounterpoint: null,
  newAnnotationPosition: {},

  counterpointLoaded: false,

  counterpointColors: {},
  counterpointColorsList: ['#E1E3EE', '#E9E4E6'],
  scrollCounter: 0,
  verticalView: false,
  verticalViewId: [],
  fullTextView: false,
  coachingMode: false,
  annotationsVisible: {},
  optionTrue: false,

  pendingAnnotationFocus: null,
  shouldClearSelection: false,
};

//
// getters
const getters = {
  currentTeamLatestRoundCounterpoints: state => {
    let documentTeam = state.debateStatus?.team;
    let teamCounterpoints = [
      ...state.counterpointData?.rounds?.filter(
        cpData => getId(documentTeam) === getId(cpData.team)
      ),
    ];
    //
    // get counterpoints for latest round
    return teamCounterpoints.sort((cp1, cp2) => cp2.round - cp1.round)[0];
  },

  getVisibleRounds: () => rounds => {
    let data = rounds.filter(r => r.round > 0);
    return data.length > 4 ? data.slice(data.length - 4) : data;
  },

  roundCount: state => state.counterpointData?.rounds?.length || 0,

  getAnnotation: state => refId =>
    state.counterpointData.counterpointsFlat?.find(ann => ann._id === refId),

  getAnnotationRound: state => annotationId => {
    return state.counterpointData?.rounds?.find(cp =>
      cp.annotations?.some(ann => ann._id === annotationId)
    );
  },

  getAnnotationChildCount: state => parentAnnotationId => {
    return (
      state.counterpointData?.counterpointsFlat?.filter(
        ann => ann?.refAnnotation?._id === parentAnnotationId
      ).length || 0
    );
  },

  lastRoundNumber: state => {
    const rounds = state.counterpointData?.rounds;
    return rounds?.length ? rounds[rounds.length - 1].round : 0;
  },

  refByAnnotationsMap: state => state.counterpointData?.refByAnnotationsMap || {},

  counterpointsFlat: state => state.counterpointData?.counterpointsFlat || [],

  getColor: state => index =>
    state.counterpointColorsList[index % state.counterpointColorsList.length],
  getCounterpointColor: state => counterpoint => state.counterpointColors[getId(counterpoint.team)],

  allRounds: state =>
    [state.counterpointData.roundZero, ...state.counterpointData.rounds]?.sort(
      (a, b) => a.round - b.round
    ),
  roundStatus: state => {
    return state.debateNextState?.team_round_status || state.debateNextState?.current_status;
  },
  roundPublished: (state, getters) => roundNumber => {
    if (!state.counterpointLoaded) return false;
    const round = getters.allRounds?.find(r => r.round === roundNumber);
    return round.status === RoundStatus.PUBLISHED;
  },
  roundActive: (state, getters) => roundNumber => {
    const isCohort = getters.permissionLevel === PermissionLevel.COHORT;
    if (isCohort) return true;
    return !getters.roundPublished(roundNumber);
  },
  myLatestRoundActive: (state, getters) => {
    return getters.roundStatus !== RoundStatus.PUBLISHED;
  },
  myLatestRound: (state, getters) => {
    if (!state.counterpointData?.rounds?.length) return null;

    if (getters.permissionLevel === PermissionLevel.HOST) return state.debateStatus.rounds[0];

    const myRoundNumber = getters.myLatestRoundNumber;

    // If myLatestRoundNumber is undefined, null, or 0, use the last available round
    if (!myRoundNumber || myRoundNumber === 0) {
      return state.counterpointData.rounds[state.counterpointData.rounds.length - 1];
    }

    const round = state.counterpointData.rounds.find(cp => cp.round === myRoundNumber);

    // Fallback: if we can't find the specific round (e.g., in outline debates),
    // return the last available round
    if (!round && state.counterpointData.rounds.length > 0) {
      return state.counterpointData.rounds[state.counterpointData.rounds.length - 1];
    }

    return round;
  },
  myResponseRound: (state, getters) => {
    if (!state.counterpointData?.rounds?.length) return null;

    if (getters.permissionLevel === PermissionLevel.HOST) return state.debateStatus.rounds[0];

    const isCurrentUserCohort = getters.permissionLevel === PermissionLevel.COHORT;

    const hasTeamMemberWithCohort = state.currentTeam?.members?.some(
      member =>
        (member.status === 'Accepted' || member.status === 'Invited') &&
        member.teamRole === PermissionLevel.COHORT
    );

    const isCohortTeam = isCurrentUserCohort || hasTeamMemberWithCohort;

    if (isCohortTeam) {
      const unpublishedEvenRounds = state.counterpointData.rounds.filter(
        cp => cp.round % 2 === 0 && cp.status !== 'Published'
      );

      if (unpublishedEvenRounds.length > 0) {
        return unpublishedEvenRounds[0];
      }
    }

    const myLatestRoundData = getters.myLatestRound;

    if (myLatestRoundData && myLatestRoundData.round % 2 === 1) {
      return myLatestRoundData;
    }

    const unpublishedEvenRounds = state.counterpointData.rounds.filter(
      cp => cp.round % 2 === 0 && cp.status !== 'Published'
    );

    if (unpublishedEvenRounds.length > 0) {
      return unpublishedEvenRounds[0];
    }

    const reversedRounds = state.counterpointData.rounds.slice().reverse();
    const lastEvenRound = reversedRounds.find(cp => cp.round % 2 === 0);

    // Fallback: if no even round found (e.g., outline debates with only round 1),
    // return the last available round (which would be round 1)
    if (!lastEvenRound && state.counterpointData.rounds.length > 0) {
      console.warn('No even round found, using last available round');
      return state.counterpointData.rounds[state.counterpointData.rounds.length - 1];
    }

    return lastEvenRound;
  },
  myLatestRoundNumber: state => {
    // added current_round field as a temporary resource while the API is fully updated to the new team_latest_round field name
    return state.debateNextState?.team_latest_round || state.debateNextState?.current_round;
  },
  latestRound: (state, getters) => {
    if (!state.counterpointLoaded && !state.counterpointData?.rounds) return null;

    const rounds = getters.allRounds;
    return rounds?.at(-1);
  },
  roundFollowingMyLatestRound: (state, getters) => {
    if (!state.counterpointLoaded) return null;
    return state.counterpointData.rounds.find(cp => cp.round === getters.myLatestRoundNumber + 1);
  },

  teamAnnotationsSubmitted: (state, getters) => {
    return getters.permissionLevel === PermissionLevel.COHORT && getters.teamMemberData?.submitted;
  },
};

//
// mutations
const mutations = {
  setCounterpointLoaded: (state, value) => (state.counterpointLoaded = value),

  setNewAnnotationPosition: (state, value) => {
    state.newAnnotationPosition = value;
  },
  setAnnotationTop: (state, ann) => {
    state.annotationsVisible = {
      ...state.annotationsVisible,
      [ann._id]: { ...state.annotationsVisible[ann._id], top: ann.value },
    };
  },
  setAnnotationvisible: (state, ann) => {
    state.annotationsVisible = {
      ...state.annotationsVisible,
      [ann._id]: { ...state.annotationsVisible[ann._id], visible: ann.value },
    };
  },

  setVerticalView: (state, value) => {
    state.verticalView = value;
  },
  setVerticalViewId: (state, value) => {
    state.verticalViewId = value;
  },
  setFullTextView: (state, value) => {
    state.fullTextView = value;
  },

  clearNewAnnotationPosition: state => (state.newAnnotationPosition = {}),
  incrementScrollCounter: state => (state.scrollCounter += 1),
  setCoachingMode: (state, value) => (state.coachingMode = value),

  setRouteCounterpointData: (state, value) => {
    state.counterpointData.rounds = [];
    state.counterpointData.document = {};
    state.counterpointData.team = {};
    state.counterpointData.permissionLevel = value;
    state.counterpointData.active = false;
  },
  setOptionTrue: (state, value) => {
    state.optionTrue = value;
  },

  setPendingAnnotationFocus: (state, value) => (state.pendingAnnotationFocus = value),
  setShouldClearSelection: (state, value) => (state.shouldClearSelection = value),

  setSelectedCounterpoint: (state, counterpoint) => {
    state.selectedCounterpoint = counterpoint;
  },
  setCounterpointData: (state, data) => {
    state.counterpointData = data;
  },
  setCounterpointWholeData: (state, data) => {
    state.counterpointWholeData = data;
  },
  setCounterpointColors: (state, colors) => {
    state.counterpointColors = colors;
  },
  setScrollCounter: (state, value) => {
    state.scrollCounter = value;
  },
  setDebateNextStateCurrentRound: (state, roundNumber) => {
    state.debateNextState.current_round = roundNumber;
  },
  setCounterpointDataRounds: (state, rounds) => {
    if (Array.isArray(rounds)) {
      state.counterpointData.rounds = rounds;
    } else {
      state.counterpointData.rounds.splice(state.counterpointData.rounds.length, 0, rounds);
    }
  },
  setRoundZeroStatus: (state, status) => {
    if (!state.counterpointData.roundZero) {
      state.counterpointData.roundZero = {};
    }
    state.counterpointData.roundZero.status = status;
  },
  setDebateNextStateCurrentStatus: (state, status) => {
    state.debateNextState.current_status = status;
  },
  setDebateNextStateActions: (state, actions) => {
    state.debateNextState.actions = actions;
  },
  setLastRoundStatus: (state, status) => {
    if (state.counterpointData.rounds?.length) {
      state.counterpointData.rounds.at(-1).status = status;
    }
  },

  setCounterpointDataAnnotationsMap: (state, annotationsMap) => {
    state.counterpointData.annotationsMap = annotationsMap;
  },
  setCounterpointDataRefByAnnotationsMap: (state, refByAnnotationsMap) => {
    state.counterpointData.refByAnnotationsMap = refByAnnotationsMap;
  },
  setCounterpointDataCounterpointsFlat: (state, counterpointsFlat) => {
    state.counterpointData.counterpointsFlat = counterpointsFlat;
  },
};

//
// actions
const actions = {
  setSelectedAnnotation: ({ commit }, { counterpoint, annotation }) => {
    commit('setSelectedCounterpoint', counterpoint);
    commit('setSelectedItem', { type: 'annotation', item: annotation });
  },

  loadCounterpoints: async ({ state, getters, dispatch, commit }, counterpoint) => {
    try {
      //
      // if no counterpoint was provided, get it from the backend
      if (!counterpoint?._id) {
        let cpRes =
          state.publicView || state.observerStatus
            ? await dispatch('getPublicDebateStatus')
            : await dispatch('getDebateStatus');
        // assign counterpoint for evaluation
        counterpoint = cpRes;
      }
      //
      // if counterpoint data is present continue with setup
      if (!counterpoint) return false;

      // Use helper function to prepare counterpoint data
      const { roundZero, activeRoundsWhole, activeRounds } = prepareCounterpointData(counterpoint);
      counterpoint.roundZero = roundZero;

      // Set up whole counterpoint data copy
      let counterpointWholeCopy = { ...counterpoint };
      counterpointWholeCopy.rounds = activeRoundsWhole;
      commit('setCounterpointWholeData', counterpointWholeCopy);

      // Set up filtered counterpoint data copy
      let counterpointCopy = { ...counterpoint };
      counterpointCopy.rounds = activeRounds;

      filterAnnotationsForCurateMode(counterpoint, state.documentData, getters, getters.userId);

      commit('setCounterpointData', counterpointCopy);

      // Clear document cached data when counterpoint data changes
      // since the cached computations depend on counterpoint data
      commit('clearComputedData');

      if (state.publicView || getters.permissionLevel === PermissionLevel.OBSERVER) {
        let likesMap = state.counterpointData?.rounds
          .flatMap(round => round.annotations)
          .reduce((acc, annotation) => {
            const { _id, likes, dislikes } = annotation;
            acc[_id] = { likes, dislikes };
            return acc;
          }, {});
        commit('setAnnotationPublicLikesTotalsMap', likesMap);
        commit('setShowAnnotationLikes', true);
        let flagMap = state.counterpointData?.rounds
          .flatMap(round => round.annotations)
          .reduce((acc, annotation) => {
            const { _id, flag } = annotation;
            acc[_id] = flag ? flag : null;
            return acc;
          }, {});
        commit('setAnnotationPublicLikesFlag', flagMap);
      }

      if (!state.publicView && getters.permissionLevel !== PermissionLevel.HOST) {
        await dispatch('loadTeamDetails');
      }

      let latestRound = activeRounds?.length ? activeRounds[activeRounds?.length - 1]?.round : null;

      if (latestRound) {
        const { isTeamLead, activeTeamMembersIds } = setupTeamModeFiltering(
          counterpoint,
          state,
          activeRounds,
          latestRound
        );

        commit('setTeamMode', isTeamLead && activeTeamMembersIds?.length >= 1);
      }

      await convertInstructionsToComments(counterpoint, state, dispatch, latestRound);

      const counterpointRounds = state.counterpointData?.rounds;
      counterpointRounds?.forEach(cp => cp.annotations?.forEach(ann => (ann.cpId = cp._id)));

      setupTeamReplies(state, getters);

      dispatch('setupCounterpoints');
      commit('setDocumentLoaded', true);

      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  },

  setupCounterpoints: async ({ state, getters, commit }) => {
    const counterpointRounds = state.counterpointData?.rounds;

    // assing counterpoint colors
    const counterpointColors = [...new Set(counterpointRounds?.map(cp => getId(cp.team)))].reduce(
      (colorsObj, teamId, idx) => {
        return {
          ...colorsObj,
          [teamId]: getters.getColor(idx),
        };
      },
      {}
    );
    commit('setCounterpointColors', counterpointColors);

    //
    // enrich annotations data with non-circular roundCounterpoint metadata
    counterpointRounds?.forEach(cpRound => {
      cpRound?.annotations?.forEach(ann => {
        ann.roundCounterpoint = {
          _id: cpRound._id,
          round: cpRound.round,
          author: cpRound.author,
          team: cpRound.team,
          status: cpRound.status,
          created_at: cpRound.created_at,
          updated_at: cpRound.updated_at,
          debate_id: cpRound.debate_id,
        };
      });
    });
    //
    // create flat array for all annotations
    const counterpointsFlat = counterpointRounds
      ? counterpointRounds.reduce(
          (annArr, cpData) => [...annArr, ...(cpData?.annotations || [])],
          []
        )
      : [];
    commit('setCounterpointDataCounterpointsFlat', counterpointsFlat);
    //
    // create annotations map
    const annotationsMap = state.counterpointData.counterpointsFlat.reduce((obj, ann) => {
      return {
        ...obj,
        [ann._id]: ann,
      };
    }, {});
    //
    // create annotations "referenced by" map
    commit('setCounterpointDataAnnotationsMap', annotationsMap);
    const refByAnnotationsMap = state.counterpointData.counterpointsFlat
      .filter(ann => ann.refAnnotation)
      .reduce((obj, ann) => {
        return {
          ...obj,
          [ann.refAnnotation._id]: obj[ann.refAnnotation._id]
            ? [...obj[ann.refAnnotation._id], ann]
            : [ann],
        };
      }, {});
    commit('setCounterpointDataRefByAnnotationsMap', refByAnnotationsMap);

    //
    // assign annotations to document sources
    state.documentData.sources.forEach(source => {
      if ([DocumentType.ARTICLE, DocumentType.TEXT].includes(source.sourceType) && source?.nodes) {
        assignAnnotationsToNodes(source.nodes, counterpointRounds);
      } else if (
        source.sourceType === DocumentType.OUTLINE &&
        getters.documentStatus === DocumentStatus.PUBLISHED
      ) {
        assignAnnotationsToNodes(getters.documentSourceTextNodes, counterpointRounds);
      }
    });
  },

  saveTeamReplySelection: async ({ state, dispatch, commit }, selectedReplyID) => {
    let res = null;
    try {
      res = await xhr.updateAnnotation(state.selectedItem.item._id, {
        selectedReply: selectedReplyID,
      });

      commit('clearComputedData');
    } catch (err) {
      dispatch('showToastAlert', {
        title: 'Error updating annotation',
        message: err,
      });
    }
    return res;
  },

  updateTeamReplyDeselected: async ({ state, dispatch, commit }) => {
    let res = null;
    try {
      res = await xhr.updateAnnotation(state.selectedItem.item._id, { selectedReply: null });

      commit('clearComputedData');
    } catch (err) {
      dispatch('showToastAlert', {
        title: 'Error updating annotation',
        message: err,
      });
    }
    return res;
  },

  updateAnnotation: async ({ dispatch, commit }, { annotation, properties }) => {
    let res = null;
    try {
      res = await xhr.updateAnnotation(annotation._id, properties);

      commit('clearComputedData');
    } catch (err) {
      dispatch('showToastAlert', {
        title: 'Error updating annotation',
        message: err,
      });
    }

    Object.keys(properties).forEach(key => {
      annotation[key] = res[key];
    });
    //
    // reset UI

    return res;
  },

  updateTeamReply: async ({ getters, dispatch }, { annotation, newText, oldText }) => {
    let res = null;
    if (newText !== oldText) {
      const currentTeamReply = annotation.teamReplies
        ? annotation.teamReplies.find(reply => getId(reply.author) === getters.userId)
        : null;
      if (currentTeamReply && currentTeamReply._id) {
        try {
          res = await xhr.updateTeamReply(getId(currentTeamReply), { comment: newText });
          if (currentTeamReply._id !== res._id) {
            dispatch('showToastAlert', {
              title: 'Error updating team reply',
              message: 'Endpoint returned wrong id',
            });
          }
        } catch (err) {
          dispatch('showToastAlert', {
            title: 'Error updating team reply',
            message: err,
          });
        }
      } else {
        const replyToAdd = {
          ...currentTeamReply,
          comment: newText,
        };
        try {
          res = await xhr.addTeamReply(annotation._id, replyToAdd);
          annotation.teamReplies.splice(annotation.teamReplies?.length, 0, res);
          currentTeamReply._id = res._id;
          currentTeamReply.author = res.author;
          currentTeamReply.comment = res.comment;
          if (!res) {
            dispatch('showToastAlert', {
              title: 'Error updating team reply',
              message: 'Endpoint returned no data',
            });
          }
        } catch (err) {
          dispatch('showToastAlert', {
            title: 'Error adding team reply',
            message: err,
          });
        }
      }
    }
    return res;
  },

  saveAnnotationApproval: async ({ dispatch, commit }, { annotation, approved }) => {
    let res = null;
    try {
      res = await xhr.updateAnnotation(annotation._id, { approved });

      // Clear document cached data when annotation approval is updated
      commit('clearComputedData');
    } catch (err) {
      dispatch('showToastAlert', {
        title: 'Error approving annotation',
        message: err,
      });
    }
    return res;
  },

  reorderAnnotations: ({ state, dispatch }) => {
    if (state.counterpointData?.rounds?.length)
      state.counterpointData.rounds.forEach(cpData => {
        dispatch('sortAnnotationsByNodeOrder', cpData.annotations);
      });
  },

  sortAnnotationsByNodeOrder: ({ getters }, annotations) => {
    annotations.sort((a, b) => {
      const getSelectedText = annotation => {
        let selectedText = annotation.selectedText;
        if (annotation.refAnnotation?._id) {
          let parent = getters.getAnnotation(getId(annotation.refAnnotation?._id));
          while (parent?.refAnnotation?._id) {
            parent = getters.getAnnotation(getId(parent.refAnnotation?._id));
          }
          if (parent?.selectedText) {
            selectedText = parent.selectedText;
          }
        }
        return selectedText;
      };
      const selectedText1 = getSelectedText(a);
      const selectedText2 = getSelectedText(b);

      if (selectedText1?.from && selectedText2?.from) {
        const nodeDiff = selectedText1.from.nodeOrder - selectedText2.from.nodeOrder;
        if (nodeDiff !== 0) {
          return nodeDiff;
        } else {
          return selectedText1.from.textPosition - selectedText2.from.textPosition;
        }
      } else {
        return 0;
      }
    });

    const idOrder = [...new Set(annotations.map(obj => getId(obj.refAnnotation?._id || obj)))];
    annotations.sort(
      (a, b) =>
        idOrder.indexOf(getId(a.refAnnotation?._id || a)) -
        idOrder.indexOf(getId(b.refAnnotation?._id || b))
    );
  },

  createNewHostInlineRule: async ({ state, getters, dispatch }, type) => {
    //
    // set selected text from document
    //
    // create new annotation model
    const newRuleModel = {
      comment: ' ',
      instruction: ' ',
      author: { _id: getters.userId },
      level: getters.permissionLevel,
      type,
      selectedText: state.newAnnotationPosition,
    };
    //
    // save annotation to DB
    const debateID = state.counterpointData._id;
    const roundsIDs = state.counterpointData.rounds.map(r => r._id);
    const res = await xhr.addHostInlineRule(debateID, roundsIDs, newRuleModel);
    //
    // reload counterpoint data
    await dispatch('loadCounterpoints');
    //
    // reset UI
    if (state.selectedItem.type === 'outlineNode') dispatch('loadOutlineTree');
    //
    dispatch('reorderAnnotations');
    //
    // return new annotation id
    const roundAnnotations = res.rounds[0].annotations;
    const newAnnotationId = roundAnnotations[roundAnnotations.length - 1]._id;
    return newAnnotationId;
  },

  saveAnnotation: (context, { debateID, debateRoundID, annotation }) => {
    return xhr.addAnnotation(debateID, debateRoundID, annotation);
  },

  createNewAnnotation: async (
    { state, getters, dispatch, commit },
    { annotationType, reponseRoundAnn }
  ) => {
    //
    // get counterpoint for the current user
    // let round = getters.myLatestRound;
    let round = reponseRoundAnn ? getters.myResponseRound : getters.myLatestRound;

    // Check if round exists (can be null in outline debates or before counterpoint data is loaded)
    if (!round) {
      console.warn('Cannot create annotation: round data not available');
      return false;
    }

    const isCurrentUserCohort = getters.permissionLevel === PermissionLevel.COHORT;

    const hasTeamMemberWithCohort = state.currentTeam?.members?.some(
      member =>
        (member.status === 'Accepted' || member.status === 'Invited') &&
        member.teamRole === PermissionLevel.COHORT
    );

    const isCohortTeam = isCurrentUserCohort || hasTeamMemberWithCohort;

    const isRespondingToAnnotation =
      state.selectedItem.type === 'annotation' && state.selectedItem.item._id;
    const isAnnotatingArticleDirectly = !isRespondingToAnnotation;

    if (isCohortTeam && isRespondingToAnnotation) {
      const targetAnnotation = state.selectedItem.item;
      const targetRound = targetAnnotation.roundCounterpoint?.round;

      if (targetRound !== undefined) {
        const nextRoundNumber = targetRound + 1;
        const nextRound = state.counterpointData?.rounds?.find(r => r.round === nextRoundNumber);

        if (nextRound) {
          // Check if the next round is within visible rounds
          const visibleRounds = getters.getVisibleRounds(state.counterpointData.rounds);
          const isNextRoundVisible = visibleRounds.some(r => r.round === nextRoundNumber);

          if (!isNextRoundVisible) {
            console.warn(
              `Cannot create annotation: round ${nextRoundNumber} is not visible on screen`
            );
            return false;
          }

          round = nextRound;
        } else {
          // Next round doesn't exist - can't add annotation to highest round
          console.warn(
            `Cannot create annotation: round ${nextRoundNumber} does not exist (trying to annotate highest round)`
          );
          return false;
        }
      }
    } else if (isCohortTeam && isAnnotatingArticleDirectly && !reponseRoundAnn) {
      const round1 = state.counterpointData?.rounds?.[0];
      if (round1) {
        round = round1;
      }
    }

    const maxRoundsForArticlePanel = isCohortTeam ? 4 : 3;

    if (isAnnotatingArticleDirectly && round.round === maxRoundsForArticlePanel) {
      return false;
    }
    //
    // create new annotation model
    const newAnnotation = {
      comment: ' ',
      author: { _id: getters.userId },
      level: getters.permissionLevel,
      type: annotationType,
    };
    //
    // add placeholder for instruction text
    if (annotationType === 'instruction') newAnnotation.instruction = ' ';
    //
    // set selected text from document
    newAnnotation.selectedText = state.newAnnotationPosition;
    //
    // set reference annotation
    if (state.selectedItem.item._id) {
      switch (state.selectedItem.type) {
        case 'annotation':
          newAnnotation.refAnnotation = { _id: state.selectedItem.item._id };
          break;
        case 'outlineNode':
        case 'textNode':
          break;
        default:
          throw new Error('UI Error: Invalid annotation citation type');
      }
    }
    //
    // save annotation to DB
    const debateID = state.counterpointData._id;
    const debateRoundID = round._id;

    try {
      commit('setShouldClearSelection', true);

      // Save annotation to database
      const res = await xhr.addAnnotation(debateID, debateRoundID, newAnnotation);

      const annotation = {
        ...res,
        active: true,
      };

      // Append annotation to annotations array
      if (round.annotations) {
        round.annotations[round.annotations.length] = annotation;
      } else {
        round.annotations = [annotation];
      }

      dispatch('setupAnnotation', {
        annotation,
        round,
      });

      // Set visibility for the annotation
      commit('setAnnotationvisible', { _id: annotation._id, value: true });
      commit('setAnnotationTop', {
        _id: annotation._id,
        value: state.annotationsVisible[annotation._id]?.top || 0,
      });

      // Re-setup counterpoints to ensure annotation is included in flat arrays and maps
      dispatch('setupCounterpoints');

      // Clear document cached data so annotation is included in renders
      commit('clearComputedData');

      // Clear inline node annotations cache so reference highlights update
      commit('clearInlineNodeAnnotationsCache');

      EventBus.emit('counterpoint.data.updated');
      EventBus.emit('annotation.active');
      EventBus.emit('annotation.created', annotation._id);
      EventBus.emit('UI.position.annotations', 0, annotation._id, false);

      dispatch('setSelectedAnnotation', {
        counterpoint: round,
        annotation,
      });

      // Refetch parent annotation if this is a reply to update canUpdateOrDelete
      if (res.refAnnotation?._id) {
        try {
          const parentAnnotation = await xhr.getAnnotation(res.refAnnotation._id);
          // Find and update the parent annotation in the store
          const parentRound = state.counterpointData?.rounds?.find(r =>
            r.annotations?.some(ann => ann._id === res.refAnnotation._id)
          );
          if (parentRound) {
            const parentIndex = parentRound.annotations.findIndex(
              ann => ann._id === res.refAnnotation._id
            );
            if (parentIndex !== -1) {
              // Update canUpdateOrDelete property on parent
              parentRound.annotations[parentIndex].canUpdateOrDelete =
                parentAnnotation.canUpdateOrDelete;
            }
          }
        } catch (parentErr) {
          console.error('[ANNOTATION_DEBUG] Error refetching parent annotation:', parentErr);
        }
      }

      // Focus the annotation after we have the real ID from the server
      const focusData = {
        annotationId: annotation._id,
        isVertical: state.verticalView,
        isNew: true,
      };
      setTimeout(() => {
        commit('setPendingAnnotationFocus', focusData);
      }, 200); // allow animation to complete

      // Return annotation id
      return annotation._id;
    } catch (err) {
      dispatch('showToastAlert', {
        title: 'Error saving new annotation',
        message: err,
      });

      return null;
    }
  },

  setupAnnotation({ state, dispatch }, { annotation, round }) {
    annotation.cpId = round._id;
    //
    // reset UI
    dispatch('setupCounterpoints');
    //
    if (state.selectedItem.type === 'outlineNode') dispatch('loadOutlineTree');
    //
    // dispatch('reorderAnnotations');
    //
    // return annotation id
    return annotation?._id;
  },

  addDummyRound({ state, getters, commit }) {
    commit('setOptionTrue', false);
    const roundNumber = state.counterpointData?.rounds?.length + 1;
    const tempRound = {
      _id: `temp-${roundNumber}`,
      author: { _id: getters.userId },
      team: { _id: getters.teamID },
      created_at: new Date(),
      debate_id: state.debateStatus._id,
      round: 1,
      status: RoundStatus.FINALIZING,
      updated_at: new Date(),
      annotations: [],
    };
    if (state.counterpointData?.rounds?.length) {
      commit('setCounterpointDataRounds', [tempRound]);
    } else {
      commit('setCounterpointDataRounds', tempRound);
    }
    commit('setDebateNextStateCurrentRound', roundNumber);
    commit('setCounterpointLoaded', true);
    return state.counterpointData.rounds;
  },

  updateDummyRound({ state, dispatch, commit }, newRound) {
    const tempRound = state.counterpointData.rounds.at(-1) || {};
    // if (!tempRound._id.match('temp-')) return;
    tempRound._id = newRound._id;
    tempRound.annotations?.forEach(async ann => {
      const isInstruction = ['instruction', 'hostInstruction'].includes(ann.type);
      const textField = isInstruction ? 'instruction' : 'comment';
      ann[textField] = ann.updatedText;
      const resAnnotation = await dispatch('saveAnnotation', {
        debateID: state.counterpointData._id,
        debateRoundID: newRound._id,
        annotation: {
          ...ann,
          // clear temp ID to avoid DB conflicts
          _id: undefined,
          // clear UI bound fields
          cpId: undefined,
          roundCounterpoint: undefined,
        },
      });
      ann._id = resAnnotation._id;
      ann.cpId = newRound._id;
      ann.roundCounterpoint = {
        _id: tempRound._id,
        round: tempRound.round,
        author: tempRound.author,
        team: tempRound.team,
        status: tempRound.status,
        created_at: tempRound.created_at,
        updated_at: tempRound.updated_at,
        debate_id: tempRound.debate_id,
      };
      ann.level = resAnnotation.level;
    });
    commit('setOptionTrue', true);
  },

  simulateContinueToNextRound({ state, commit }) {
    //
    // update last round status
    if (state.debateNextState.current_round === 0) {
      commit('setRoundZeroStatus', RoundStatus.PUBLISHED);
      commit('setDebateNextStateCurrentStatus', RoundStatus.PUBLISHED);
      commit('setDebateNextStateActions', ['Awaiting Replies']);
    } else if (state.debateNextState.current_round === 1) {
      if (state.documentData.initialStance === 'Host') {
        commit('setRoundZeroStatus', RoundStatus.PUBLISHED);
        commit('setDebateNextStateCurrentStatus', RoundStatus.FINALIZING);
      } else {
        commit('setRoundZeroStatus', RoundStatus.PUBLISHED);
        commit('setDebateNextStateCurrentStatus', RoundStatus.PUBLISHED);
        // state.debateNextState.current_round = state.debateNextState.current_round + 1;
      }
      commit('setDebateNextStateActions', ['Loading Actions']);
    } else {
      commit('setLastRoundStatus', RoundStatus.PUBLISHED);
      commit('setDebateNextStateCurrentStatus', RoundStatus.PUBLISHED);
      commit('setDebateNextStateActions', ['Awaiting Replies']);
    }
  },
};

//
// exports
export default {
  // namespaced: false,
  state,
  getters,
  actions,
  mutations,
};
